import api from './api';

export type DispatchFileType =
  | 'appt_confirmation'
  | 'gate_pass'
  | 'pickup_instruction'
  | 'delivery_instruction'
  | 'facility_notice'
  | 'other';

export const DISPATCH_FILE_TYPE_LABELS: Record<DispatchFileType, string> = {
  appt_confirmation: 'Appt Confirmation',
  gate_pass: 'Gate Pass',
  pickup_instruction: 'Pickup Instruction',
  delivery_instruction: 'Delivery Instruction',
  facility_notice: 'Facility Notice',
  other: 'Other',
};

export interface DispatchFile {
  id: number;
  task_id: number;
  file_type: DispatchFileType;
  file_url: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  notes: string | null;
  uploaded_at: string;
  uploaded_by: number | null;
  uploader_name: string | null;
  uploader_role: string | null;
}

// Base URL (without /api) for building absolute download URLs with auth header
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

export const taskDispatchFileApi = {
  async list(taskId: number): Promise<DispatchFile[]> {
    const response = await api.get(`/tasks/${taskId}/dispatch-files`);
    // interceptor strips { success, data } → returns data directly
    return response.data as unknown as DispatchFile[];
  },

  async upload(
    taskId: number,
    file: File,
    fileType: DispatchFileType,
    notes?: string
  ): Promise<DispatchFile> {
    const form = new FormData();
    form.append('file', file);
    form.append('file_type', fileType);
    if (notes) form.append('notes', notes);
    const response = await api.post(`/tasks/${taskId}/dispatch-files/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data as unknown as DispatchFile;
  },

  async delete(taskId: number, fileId: number): Promise<void> {
    await api.delete(`/tasks/${taskId}/dispatch-files/${fileId}`);
  },

  /**
   * Fetch protected download as a Blob and trigger a browser download.
   * We cannot use a plain <a href> because the endpoint requires a Bearer token.
   */
  async download(taskId: number, file: DispatchFile): Promise<void> {
    const response = await api.get(
      `/tasks/${taskId}/dispatch-files/${file.id}/download`,
      { responseType: 'blob' }
    );
    // With responseType=blob the interceptor's success-extraction is bypassed.
    const blob = response.data as unknown as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Open protected file inline in a new tab (for PDF/image preview).
   */
  async view(taskId: number, file: DispatchFile): Promise<void> {
    const response = await api.get(
      `/tasks/${taskId}/dispatch-files/${file.id}/download`,
      { params: { inline: 'true' }, responseType: 'blob' }
    );
    const blob = response.data as unknown as Blob;
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Revoke after a short delay so the new tab has time to load
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },
};

// Silence unused-import warning for API_BASE if we later need it
void API_BASE;

export default taskDispatchFileApi;
